/**
 * Bridges app-internal markdown links and Streamdown's custom-tag rendering.
 *
 * The assistant links to pages of the app with a root-relative markdown link,
 * e.g. `[Éditeurs logiciels Montpellier](/listing/<uuid>)`. Rendered as a plain
 * anchor, Streamdown treats it as an outbound URL: it underlines it and asks the
 * user to confirm opening an "external link" in a new tab. Turning each such
 * link into an `<applink>` element lets `MarkdownAppLink` render it as an inline
 * badge that navigates through the router, while genuine external links keep
 * Streamdown's default anchor and its confirmation modal.
 */

// A complete markdown link whose target is a root-relative path. The leading
// `!` is captured so image syntax (`![alt](/path.png)`) can be left alone.
const APP_LINK_REGEX = /(!?)\[([^\]\n]+)\]\((\/[^\s)]*)\)/g;

/**
 * Whether a markdown href really stays inside the app.
 *
 * Resolved rather than pattern-matched, because a leading slash proves nothing:
 * inside a special scheme the URL parser reads `\` as `/`, so `/\host` and
 * `/\/host` are protocol-relative and leave the origin while still passing a
 * `startsWith("/")` test. The parser is the only thing that agrees with where
 * the browser will actually navigate.
 */
export function isSameOriginPath(href: string): boolean {
  if (!href.startsWith("/")) return false;
  try {
    return (
      new URL(href, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Replaces markdown links pointing to an app route with `<applink>` elements.
 * Any other link — and any image — is left untouched.
 */
export function transformAppLinks(text: string): string {
  return text.replace(
    APP_LINK_REGEX,
    (fullMatch, bang: string, label: string, href: string) => {
      if (bang) return fullMatch;
      if (!isSameOriginPath(href)) return fullMatch;

      return `<applink href="${escapeHtml(href)}">${escapeHtml(label)}</applink>`;
    },
  );
}
